import type { LumpsumAllocationCategoryExecutionStatus } from '../../api/allocation.types'
import type {
  LumpsumAllocationBuyReason,
  LumpsumAllocationSellReason
} from '../api/allocationRecommendation.types'
import type {
  LumpsumPlanRecommendationHighExposure,
  LumpsumPlanRecommendationOverDiversification,
  LumpsumPlanRecommendationOverExposure,
  LumpsumPlanRecommendationTaxData,
  LumpsumPlanTransferType,
  LumpsumPlanRecommendationUnderPerformance
} from '../api/recommendation.types'

/**
 * One fund in a switch pair — either exiting (sold) or entering (bought).
 * Amounts are rounded like fund cards.
 * - exitingFund: sellAmount is the redeem / tax withdrawal ₹.
 * - enteringFund: buyAmount is that fund's buy ₹.
 */
export type PlanRecommendationPairFundDetails = {
  fundName: string
  isin: string
  categoryName: string
  powerRank: number | null
  powerRating: string | null
  mfLogoUrl: string
  /** Holdings value for missed-gains graph; only meaningful on the exiting fund. */
  currentValue: number
  /** Sell / redeem ₹ — only on exitingFund. */
  sellAmount?: number
  /** Buy ₹ — only on enteringFund. */
  buyAmount?: number
  transferType: LumpsumPlanTransferType | null
  swpAmount: number | null
  swpDurationInMonths: number | null
  executed: boolean
  taxData: LumpsumPlanRecommendationTaxData | null
  categoryAllocationExecutionStatus: LumpsumAllocationCategoryExecutionStatus | string | null
  categoryFundCountExecutionStatus: LumpsumAllocationCategoryExecutionStatus | string | null
}

/**
 * Which side of the pair is the underline-tab (counter) fund for this panel.
 * Buy-entry sheets: sell tabs → EXITING. Sell-entry sheets: buy tabs → ENTERING.
 */
export enum PlanRecommendationTabFundSide {
  EXITING = 'exiting',
  ENTERING = 'entering'
}

/**
 * Normalized per-tab view for a fund switch recommendation.
 * Mappers resolve header vs tab fund into stable exiting/entering funds so UI
 * below the body never branches on buy-entry vs sell-entry sheet types.
 *
 * Accordion amounts: both legs from the counter/tab fund only (header mappers set identity, not ₹).
 * Sell-sheet outside tax: root totalTransferAmount. Buy-sheet outside tax: that sell tab’s sellAmount.
 */
export type PlanRecommendationPairView = {
  planSnapshotDate: number | null
  /** Underline-tab fund side for this panel — use `getPlanRecommendationTabFund`. */
  tabFundSide: PlanRecommendationTabFundSide
  exitingFund: PlanRecommendationPairFundDetails
  enteringFund: PlanRecommendationPairFundDetails
  sellReasons: LumpsumAllocationSellReason[] | null
  buyReasons: LumpsumAllocationBuyReason[] | null
  underPerformance: LumpsumPlanRecommendationUnderPerformance | null
  highExposure: LumpsumPlanRecommendationHighExposure | null
  overExposure: LumpsumPlanRecommendationOverExposure | null
  overDiversification: LumpsumPlanRecommendationOverDiversification | null
}

export function getPlanRecommendationTabFund(
  pair: PlanRecommendationPairView
): PlanRecommendationPairFundDetails {
  if (pair.tabFundSide === PlanRecommendationTabFundSide.ENTERING) {
    return pair.enteringFund
  }
  return pair.exitingFund
}
